const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_QUALITY = 0.82;
const COMPRESSIBLE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);

const replaceExtension = (name, nextExt) => {
  const safeName = String(name || "image").replace(/\.[^.]+$/, "");
  return `${safeName || "image"}${nextExt}`;
};

const loadImageElement = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("이미지를 읽을 수 없습니다."));
    };
    image.src = url;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("이미지를 압축하지 못했습니다."));
      },
      type,
      quality
    );
  });

export const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value}B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)}KB`;
  return `${(value / 1024 / 1024).toFixed(1)}MB`;
};

export const prepareImageUploadFile = async (
  file,
  {
    maxDimension = DEFAULT_MAX_DIMENSION,
    quality = DEFAULT_QUALITY,
    outputType = "image/jpeg",
  } = {}
) => {
  if (!file) return null;
  if (!String(file.type || "").startsWith("image/")) {
    throw new Error("이미지 파일만 업로드할 수 있습니다.");
  }

  if (!COMPRESSIBLE_TYPES.has(String(file.type || "").toLowerCase())) {
    return {
      file,
      originalFile: file,
      compressed: false,
      originalSize: file.size,
      compressedSize: file.size,
    };
  }

  const image = await loadImageElement(file);
  const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  if (scale === 1 && file.size <= 1024 * 1024) {
    return {
      file,
      originalFile: file,
      compressed: false,
      originalSize: file.size,
      compressedSize: file.size,
    };
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const blob = await canvasToBlob(canvas, outputType, quality);
  if (!blob || blob.size >= file.size) {
    return {
      file,
      originalFile: file,
      compressed: false,
      originalSize: file.size,
      compressedSize: file.size,
    };
  }

  const compressedFile = new File([blob], replaceExtension(file.name, ".jpg"), {
    type: outputType,
    lastModified: Date.now(),
  });

  return {
    file: compressedFile,
    originalFile: file,
    compressed: true,
    originalSize: file.size,
    compressedSize: compressedFile.size,
  };
};
